/**
 * Öffentliche KI-Chat-Seite (KEIN Login) — Route /chat. Führt einen Anfrage-Funnel:
 * beantwortet Fragen aus der Wissensbasis (RAG) und nimmt die Anfrage auf. Same-origin-Fetch
 * auf POST /api/public/chat (Tenant aus der Subdomain). Erste Assistenten-Begrüßung nur in der UI.
 *
 * Backlog #41: optionaler Bild-Anhang (Referenzfoto). Upload läuft SOFORT beim Auswählen gegen
 * POST /api/public/chat/upload (R2 "staging/") - die KI selbst sieht/braucht den Anhang nicht,
 * er ist rein fürs Team später am entstehenden Vorgang sichtbar. Die zurückgelieferte `uploadId`
 * wird nur clientseitig gehalten und mit JEDER weiteren Chat-Nachricht mitgeschickt, bis eine
 * Anfrage entsteht (server-seitiges "Claimen" - siehe public-chat.ts) - danach geleert.
 *
 * Bot-Schutz (Cloudflare Turnstile): DORMANT by default. Nur wenn VITE_TURNSTILE_SITE_KEY gesetzt
 * ist, wird das Turnstile-Skript nachgeladen, ein Widget gerendert und dessen Token mit JEDEM
 * Request (Chat + Upload) mitgeschickt. Ohne Site-Key ändert sich nichts: kein Skript, kein
 * Widget, kein Token - exakt das bisherige Verhalten. Serverseitig prüft lib/turnstile.ts.
 */
import { useEffect, useRef, useState } from "react";
import { BetriebsKopf } from "../ui/BetriebsKopf";
import { Button, Textarea } from "../ui/components";

interface Msg {
  role: "user" | "assistant";
  content: string;
}

/**
 * Minimaler Typ fürs global per Skript geladene Cloudflare-Turnstile-API (kein npm-Dep).
 * Nur die tatsächlich genutzten Methoden/Optionen.
 */
interface TurnstileRenderOptions {
  sitekey: string;
  callback?: (token: string) => void;
  "expired-callback"?: () => void;
  "error-callback"?: () => void;
  language?: string;
}
interface TurnstileApi {
  render: (element: HTMLElement, options: TurnstileRenderOptions) => string;
  reset: (widgetId?: string) => void;
  remove: (widgetId?: string) => void;
}
declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const MAX_ATTACHMENTS = 5;

/**
 * Punkt 41 (Folge): die festen Texte der Gastseite auf Deutsch und Englisch — dasselbe Muster wie
 * `T`/`lang` in pages/offer-public.tsx. Einen Vorgang gibt es hier noch nicht; die Sprache ist die
 * Anfragesprache: `?lang=en|de` (Einbettung auf einer englischen Website), sonst die Sprache des
 * Browsers, sonst Deutsch. Die Antworten der KI folgen ohnehin der Sprache des Gastes.
 */
const T = {
  de: {
    greeting: "Hallo! Ich helfe Ihnen gern bei Ihrer Event- oder Catering-Anfrage. Worum geht es – Anlass, Datum, Personenzahl?",
    turnstileHint: "Bitte bestätige kurz, dass du kein Bot bist.",
    uploadError: "Anhang konnte nicht hochgeladen werden.",
    problem: "Entschuldigung, es gab ein Problem. Bitte versuchen Sie es erneut.",
    connection: "Entschuldigung, es gab ein Verbindungsproblem. Bitte versuchen Sie es erneut.",
    title: "Anfrage-Assistent",
    aiNotice: "Sie chatten mit einer KI. Stellen Sie Ihre Fragen – wir nehmen Ihre Anfrage direkt auf.",
    done: "✓ Ihre Anfrage ist bei uns eingegangen – wir melden uns in Kürze.",
    doneHint: "Sie können dieses Fenster jetzt schließen oder unten weitere Fragen stellen.",
    attached: (n: number) => `${n} Bild${n > 1 ? "er" : ""} angehängt`,
    attach: "Bild anhängen",
    placeholder: "Ihre Nachricht …",
    send: "Senden",
  },
  en: {
    greeting: "Hello! I'm happy to help with your event or catering enquiry. What is it about – occasion, date, number of guests?",
    turnstileHint: "Please quickly confirm that you're not a bot.",
    uploadError: "The attachment could not be uploaded.",
    problem: "Sorry, something went wrong. Please try again.",
    connection: "Sorry, there was a connection problem. Please try again.",
    title: "Enquiry assistant",
    aiNotice: "You are chatting with an AI. Ask us anything – we'll take down your enquiry right here.",
    done: "✓ We have received your enquiry – we'll be in touch shortly.",
    doneHint: "You can close this window now or ask further questions below.",
    attached: (n: number) => `${n} image${n > 1 ? "s" : ""} attached`,
    attach: "Attach image",
    placeholder: "Your message …",
    send: "Send",
  },
} as const;

/**
 * Die Server-Meldungen der Chat-Route sind deutsch. Auf Englisch zeigt die Seite stattdessen den
 * passenden englischen Text zum Fehlercode (bzw. zur KI-Ausweichantwort `aiUnavailable`).
 */
const EN_SERVER: Record<string, string> = {
  aiUnavailable: "Thank you for your message! Feel free to send us your enquiry via the contact form – we'll get back to you quickly.",
  rate_limited: "Too many requests – please wait a moment.",
  turnstile_failed: "Bot protection failed. Please reload the page and try again.",
  unsupported_media_type: "Only JPEG, PNG or WebP images are allowed.",
  file_too_large: "The file is larger than 4 MB.",
};

/** Anfragesprache der Chat-Seite: `?lang=` vor Browsersprache, Vorgabe Deutsch. */
export function chatSprache(search: string, browserSprache: string | undefined): "de" | "en" {
  const param = new URLSearchParams(search).get("lang");
  if (param === "de" || param === "en") return param;
  return browserSprache?.toLowerCase().startsWith("en") ? "en" : "de";
}
// Cloudflare Turnstile: offizielles API-Skript. Wird NUR geladen, wenn VITE_TURNSTILE_SITE_KEY gesetzt ist.
const TURNSTILE_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js";

/** Datei -> {mediaType, dataBase64} (ohne data:-Präfix) fürs JSON-Body-Upload. */
async function fileToBase64(file: File): Promise<{ mediaType: string; dataBase64: string }> {
  const buf = await file.arrayBuffer();
  let binary = "";
  const bytes = new Uint8Array(buf);
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return { mediaType: file.type || "application/octet-stream", dataBase64: btoa(binary) };
}

export default function PublicChatPage() {
  const [lang] = useState(() => chatSprache(window.location.search, navigator.language));
  const t = T[lang];
  const [messages, setMessages] = useState<Msg[]>([{ role: "assistant", content: t.greeting }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [attachmentIds, setAttachmentIds] = useState<string[]>([]);
  const [attachmentBusy, setAttachmentBusy] = useState(false);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  // Cloudflare Turnstile (DORMANT ohne Site-Key): Token aus dem Widget-Callback + sanfter Hinweis.
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileHint, setTurnstileHint] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const widgetRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  // Konstant pro Build. Fehlt der Key -> Turnstile bleibt komplett inaktiv (kein Widget, kein Token).
  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;

  // Turnstile-Widget nur laden/rendern, wenn ein Site-Key konfiguriert ist (sonst voll dormant).
  useEffect(() => {
    if (!siteKey) return;
    const key = siteKey;
    let widgetId: string | null = null;
    let cancelled = false;

    function renderWidget() {
      if (cancelled || !window.turnstile || !widgetRef.current || widgetId !== null) return;
      widgetId = window.turnstile.render(widgetRef.current, {
        sitekey: key,
        language: lang,
        callback: (token: string) => {
          setTurnstileToken(token);
          setTurnstileHint(null);
        },
        "expired-callback": () => setTurnstileToken(""),
        "error-callback": () => setTurnstileToken(""),
      });
      widgetIdRef.current = widgetId;
    }

    let script: HTMLScriptElement | null = null;
    if (window.turnstile) {
      renderWidget();
    } else {
      script = document.querySelector<HTMLScriptElement>(`script[src="${TURNSTILE_SRC}"]`);
      if (!script) {
        script = document.createElement("script");
        script.src = TURNSTILE_SRC;
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
      }
      script.addEventListener("load", renderWidget);
    }

    return () => {
      cancelled = true;
      script?.removeEventListener("load", renderWidget);
      if (window.turnstile && widgetId !== null) window.turnstile.remove(widgetId);
      widgetIdRef.current = null;
    };
  }, [siteKey, lang]);

  // Turnstile-Token ist einmalig (serverseitig verbraucht) -> nach jeder geprüften Aktion neu holen.
  function resetTurnstile() {
    if (!siteKey) return;
    if (window.turnstile && widgetIdRef.current !== null) window.turnstile.reset(widgetIdRef.current);
    setTurnstileToken("");
  }

  async function pickAttachment(file: File) {
    if (attachmentIds.length >= MAX_ATTACHMENTS) return;
    // Turnstile aktiv, aber noch kein Token -> sanfter Hinweis statt einer sicheren 403.
    if (siteKey && !turnstileToken) {
      setAttachmentError(t.turnstileHint);
      return;
    }
    setAttachmentBusy(true);
    setAttachmentError(null);
    try {
      const { mediaType, dataBase64 } = await fileToBase64(file);
      const res = await fetch("/api/public/chat/upload", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mediaType, dataBase64, turnstileToken: turnstileToken || undefined }),
      });
      const body = (await res.json().catch(() => null)) as { data?: { uploadId: string }; message?: string; error?: string } | null;
      if (res.ok && body?.data?.uploadId) {
        setAttachmentIds((ids) => [...ids, body.data!.uploadId]);
      } else {
        setAttachmentError((lang === "en" ? EN_SERVER[body?.error ?? ""] ?? t.uploadError : body?.message) ?? t.uploadError);
      }
    } catch {
      setAttachmentError(t.uploadError);
    } finally {
      setAttachmentBusy(false);
      resetTurnstile();
    }
  }

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, busy]);

  async function send() {
    const text = input.trim();
    if (!text || busy) return;
    // Turnstile aktiv, aber noch kein Token -> sanft hinweisen und Senden blockieren (Text bleibt erhalten).
    if (siteKey && !turnstileToken) {
      setTurnstileHint(t.turnstileHint);
      return;
    }
    const next: Msg[] = [...messages, { role: "user", content: text }];
    setMessages(next);
    setInput("");
    setBusy(true);
    try {
      // Verlauf ab der ersten Nutzer-Nachricht (die KI-API erwartet zuerst eine user-Rolle).
      const firstUser = next.findIndex((m) => m.role === "user");
      const convo = next.slice(firstUser < 0 ? 0 : firstUser).map((m) => ({ role: m.role, content: m.content }));
      const res = await fetch("/api/public/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messages: convo,
          website: honeypot,
          attachmentIds: attachmentIds.length > 0 ? attachmentIds : undefined,
          turnstileToken: turnstileToken || undefined,
        }),
      });
      const body = (await res.json().catch(() => null)) as { data?: { reply: string; inquiryCreated?: boolean; aiUnavailable?: boolean }; message?: string; error?: string } | null;
      // W3 (BUGHUNT-2026-08-15): Bei nicht-OK-Antworten schickt der Server eine deutsche,
      // gast-taugliche `message` mit (Turnstile abgelaufen, Rate-Limit) - die wurde hier verworfen,
      // und der Gast wusste z. B. nicht, dass er das Bot-Schutz-Widget neu bestaetigen muss.
      const reply =
        lang === "en"
          ? (body?.data?.aiUnavailable ? EN_SERVER.aiUnavailable : body?.data?.reply) ?? EN_SERVER[body?.error ?? ""] ?? t.problem
          : body?.data?.reply ?? body?.message ?? t.problem;
      setMessages((m) => [...m, { role: "assistant", content: reply }]);
      // Anhaenge sind jetzt (falls vorhanden) auf die Anfrage geclaimt - clientseitig leeren,
      // sie muessen nicht laenger mitgeschickt werden.
      if (body?.data?.inquiryCreated) {
        setDone(true);
        setAttachmentIds([]);
      }
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: t.connection }]);
    } finally {
      setBusy(false);
      resetTurnstile();
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
    <BetriebsKopf />
    <div className="mx-auto flex w-full max-w-2xl flex-1 flex-col px-4 py-6">
      <header className="mb-4">
        <div>
          <h1 className="font-display-lg text-lg font-bold text-on-background">{t.title}</h1>
          {/* EU AI Act (Art. 50): oeffentlicher Chatbot muss die KI-Interaktion offenlegen. */}
          <p className="font-body-sm text-body-sm text-secondary">{t.aiNotice}</p>
        </div>
      </header>

      <div className="flex-1 space-y-3 overflow-y-auto rounded-2xl border border-outline-variant/40 bg-surface-container-lowest p-4">
        {messages.map((m, i) => (
          <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
            <div
              className={
                m.role === "user"
                  ? "max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-sm bg-primary px-4 py-2.5 font-body-md text-body-md text-on-primary"
                  : "max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-bl-sm bg-surface-container-high px-4 py-2.5 font-body-md text-body-md text-on-surface"
              }
            >
              {m.content}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex justify-start">
            <div className="rounded-2xl rounded-bl-sm bg-surface-container-high px-4 py-2.5 font-body-md text-body-md text-secondary">…</div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {done && (
        /* CX-Audit 2026-07-17: expliziter Abschluss-Hinweis — vorher blieb offen, ob noch
           etwas zu tun ist. Weitere Nachrichten bleiben möglich (Eingabefeld bleibt aktiv). */
        <p className="mt-3 rounded-xl bg-tertiary-container px-4 py-2.5 text-center font-body-sm text-body-sm text-tertiary">
          {t.done}
          <span className="mt-0.5 block opacity-80">{t.doneHint}</span>
        </p>
      )}

      {attachmentIds.length > 0 && (
        <p className="mt-2 font-body-sm text-body-sm text-secondary">
          📎 {t.attached(attachmentIds.length)}
        </p>
      )}
      {attachmentError && <p className="mt-2 font-body-sm text-body-sm text-error">{attachmentError}</p>}

      {/* Cloudflare Turnstile: nur sichtbar, wenn ein Site-Key konfiguriert ist (sonst dormant). */}
      {siteKey && (
        <div className="mt-3 flex flex-col items-start gap-2">
          <div ref={widgetRef} />
          {turnstileHint && <p className="font-body-sm text-body-sm text-error">{turnstileHint}</p>}
        </div>
      )}

      <form
        className="mt-3 flex items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          void send();
        }}
      >
        {/* Honeypot (für Menschen unsichtbar). */}
        <input
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
          className="hidden"
          aria-hidden="true"
        />
        {/* Backlog #41: Bild-Anhang (optional) - Upload läuft sofort beim Auswählen. */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = "";
            if (file) void pickAttachment(file);
          }}
        />
        <Button
          type="button"
          variant="secondary" className="h-12 shrink-0 !px-3"
          title={t.attach}
          disabled={attachmentBusy || attachmentIds.length >= MAX_ATTACHMENTS}
          onClick={() => fileInputRef.current?.click()}
        >
          {attachmentBusy ? "…" : "📎"}
        </Button>
        <Textarea
          className="min-h-[48px] flex-1 resize-none"
          rows={1}
          placeholder={t.placeholder}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void send();
            }
          }}
        />
        <Button type="submit" className="h-12 shrink-0" disabled={busy || !input.trim()}>
          {t.send}
        </Button>
      </form>
    </div>
    </div>
  );
}
